import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import PasswordRequirements from './auth/PasswordRequirements';
import { isPasswordValid } from '../utils/passwordValidation';
import './GetCredits.css';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gc-page">
      <nav className="gc-nav">
        <button onClick={() => navigate('/')} style={s.brand}>
          <span style={s.logomark}>Z</span>
          <span style={s.wordmark}>Zynacular</span>
        </button>
      </nav>

      <div className="gc-success-body">
        <div style={s.card}>
          {success ? (
            <div style={s.successWrap}>
              <div style={s.successIcon}>✓</div>
              <h2 style={s.successHeading}>Password updated successfully.</h2>
              <button onClick={() => navigate('/')} style={s.submitBtn}>
                Sign in →
              </button>
            </div>
          ) : (
            <>
              <h2 style={s.heading}>Choose a new password</h2>
              <p style={s.sub}>Enter a new password for your account.</p>
              <form onSubmit={handleSubmit} style={s.form}>
                <div style={s.fieldGroup}>
                  <label style={s.label}>New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    required
                    autoComplete="new-password"
                    placeholder="••••••••"
                    style={s.input}
                  />
                  {passwordFocused && <PasswordRequirements password={password} />}
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>Confirm password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="••••••••"
                    style={s.input}
                  />
                </div>
                {error && <p style={s.error}>{error}</p>}
                <button
                  type="submit"
                  disabled={loading || !isPasswordValid(password)}
                  style={{ ...s.submitBtn, ...((loading || !isPasswordValid(password)) ? s.submitBtnDisabled : {}) }}
                >
                  {loading ? 'Please wait…' : 'Update password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  logomark: {
    fontFamily: "'Syne', sans-serif",
    fontSize: '22px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    background: 'linear-gradient(135deg, #E8EEFF 0%, #2E6AFF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1,
  },
  wordmark: {
    fontFamily: "'Outfit', system-ui, sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#E8EEFF',
  },
  card: {
    background: '#0c1120',
    border: '0.5px solid rgba(255,255,255,0.07)',
    borderRadius: '16px',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
  },
  heading: {
    fontFamily: "'Outfit', system-ui, sans-serif",
    fontSize: '22px',
    fontWeight: 800,
    color: '#E8EEFF',
    letterSpacing: '-0.02em',
    margin: '0 0 8px',
  },
  sub: {
    fontSize: '13px',
    color: '#6B7FA8',
    marginBottom: '28px',
    lineHeight: 1.55,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#6B7FA8',
  },
  input: {
    background: '#0A0F1E',
    border: '1px solid #1E2D4A',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#E8EEFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  error: {
    fontSize: '13px',
    color: '#ff6b6b',
    margin: 0,
    padding: '10px 12px',
    background: 'rgba(255,107,107,0.08)',
    borderRadius: '6px',
    border: '1px solid rgba(255,107,107,0.2)',
  },
  submitBtn: {
    marginTop: '4px',
    padding: '12px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #2E6AFF 0%, #1a4fd6 100%)',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(46,106,255,0.35)',
    width: '100%',
  },
  submitBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  successWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'center',
  },
  successIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: 'rgba(0,212,160,0.1)',
    border: '1px solid rgba(0,212,160,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    color: '#00D4A0',
    marginBottom: '4px',
  },
  successHeading: {
    fontFamily: "'Outfit', system-ui, sans-serif",
    fontSize: '18px',
    fontWeight: 700,
    color: '#E8EEFF',
    margin: 0,
    letterSpacing: '-0.01em',
  },
};
